/*
Copyright (c) 2012-2023 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { getApiUrl } from "constants/domain"
import {
  InventoryCategoryRequest,
  InventoryExportedFileRequest,
} from "types/delv-platform-api/inventoryCategory"
import {
  CreateBulkTaskRequest,
  CompleteBulkTaskRequest,
  GetBulkUploadTaskRequest,
} from "types/delv-platform-api/bulkUploadTask"
import { BULK_UPLOAD_TYPE } from "pages/menu/detail/bulkUpload/BulkUploadContext"

const NAMESPACE = "BULK_UPLOAD"
const API_BASE_URL = `${getApiUrl()}/delvplatformapi/merchant`

export const actionTypes = {
  RESET_INVENTORY_CATEGORY: `${NAMESPACE}/RESET_INVENTORY_CATEGORY`,
  FETCH_INVENTORY_CATEGORY: `${NAMESPACE}/FETCH_INVENTORY_CATEGORY`,
  FETCH_INVENTORY_EXPORTED_FILE: `${NAMESPACE}/FETCH_INVENTORY_EXPORTED_FILE`,
  CREATE_BULK_TASK: `${NAMESPACE}/CREATE_BULK_TASK`,
  COMPLETE_BULK_TASK: `${NAMESPACE}/COMPLETE_BULK_TASK`,
  FETCH_BULK_TASKS: `${NAMESPACE}/FETCH_BULK_TASKS`, // for single fetch bulk tasks
  POLLING_BULK_TASKS: `${NAMESPACE}/POLLING_BULK_TASKS`, // for polling bulk tasks
  CANCEL_POLLING_BULK_TASKS: `${NAMESPACE}/CANCEL_POLLING_BULK_TASKS`, // for stop polling bulk tasks
}

export const actions = {
  resetInventoryCategory: () => ({
    type: actionTypes.RESET_INVENTORY_CATEGORY,
  }),
  fetchInventoryCategory: (
    payload: InventoryCategoryRequest,
    merchantID: string,
    merchantGroupID: string,
    bulkUploadType: BULK_UPLOAD_TYPE = BULK_UPLOAD_TYPE.INVENTORY,
  ) => ({
    type: actionTypes.FETCH_INVENTORY_CATEGORY,
    payload: {
      request: {
        url: `${API_BASE_URL}/v1/${
          bulkUploadType === BULK_UPLOAD_TYPE.INVENTORY ? "inventory" : "menu"
        }/categories`,
        params: {
          categoryName: payload.categoryName,
          menuGroupID: payload.menuGroupID || undefined,
        },
        headers: {
          merchantID,
          merchantGroupID,
        },
      },
    },
  }),
  fetchInventoryExportedFile: (
    payload: InventoryExportedFileRequest,
    merchantID: string,
    merchantGroupID: string,
    bulkUploadType: BULK_UPLOAD_TYPE = BULK_UPLOAD_TYPE.INVENTORY,
  ) => ({
    type: actionTypes.FETCH_INVENTORY_EXPORTED_FILE,
    payload: {
      request: {
        url: `${API_BASE_URL}/v1/${
          bulkUploadType === BULK_UPLOAD_TYPE.INVENTORY
            ? "inventory/categories"
            : "items"
        }/export`,
        method: "post",
        data: payload,
        headers: {
          merchantID,
          merchantGroupID,
        },
      },
    },
  }),
  createBulkTask: (
    payload: CreateBulkTaskRequest,
    merchantID: string,
    merchantGroupID: string,
  ) => ({
    type: actionTypes.CREATE_BULK_TASK,
    payload: {
      request: {
        url: `${API_BASE_URL}/v1/bulktasks`,
        method: "post",
        data: payload,
        headers: {
          merchantID,
          merchantGroupID,
        },
      },
    },
  }),
  completeBulkTask: (
    payload: CompleteBulkTaskRequest,
    merchantID: string,
    merchantGroupID: string,
  ) => ({
    type: actionTypes.COMPLETE_BULK_TASK,
    payload: {
      request: {
        url: `${API_BASE_URL}/v1/bulktasks/${payload.taskID}/complete-uploading`,
        method: "put",
        data: payload, // for reducer usage getActionSuccess(actionTypes.COMPLETE_BULK_TASK)
        headers: {
          merchantID,
          merchantGroupID,
        },
      },
    },
    showError: true,
  }),
  fetchBulkTasks: (
    payload: GetBulkUploadTaskRequest,
    merchantID: string,
    merchantGroupID: string,
  ) => ({
    type: actionTypes.FETCH_BULK_TASKS,
    payload: {
      request: {
        url: `${API_BASE_URL}/v1/bulktasks`,
        params: payload,
        headers: {
          merchantID,
          merchantGroupID,
        },
      },
    },
  }),
  pollingBulkTasks: (
    payload: GetBulkUploadTaskRequest,
    merchantID: string,
    merchantGroupID: string,
  ) => ({
    type: actionTypes.POLLING_BULK_TASKS,
    payload: {
      request: {
        url: `${API_BASE_URL}/v1/bulktasks`,
        params: payload,
        headers: {
          merchantID,
          merchantGroupID,
        },
      },
    },
  }),
}
